import React from 'react';
import { Provider } from 'react-redux';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import gql from 'graphql-tag';
import JssProvider from 'react-jss/lib/JssProvider';
import { createGenerateClassName, jssPreset } from '@material-ui/core/styles';
import { create } from 'jss';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import configureStore from '../../store/findBobPublicStore';
import themes from '../../lib/themes';
import MultiLineValuation from '../../components/Succession/MultiLineValuation';
import SuccessionThemeProvider from '../../components/Succession/SuccessionThemeProvider';
import { ArrowLeftIcon, UserCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { S3Image } from '../../lib/s3';

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById('jss-insertion-point'),
});
const generateClassName = createGenerateClassName();

const store = configureStore();

// Query to fetch user valuation data for admin viewing/editing
const ADMIN_USER_VALUATION_QUERY = gql`
  query AdminUserValuation($user_id: ID!, $inventory_user_id: ID, $data_source: String) {
    admin_user_valuation(user_id: $user_id, inventory_user_id: $inventory_user_id, data_source: $data_source) {
      user_id
      user_name
      profile_picture
      line_inputs
      ebitda_adjustments
      has_saved_valuation
      scenario_id
      scenario_name
      valuation_result
      raw_user_fields
      data_source
      company {
        id
        country
        allow_user_to_specify_revenue_growth_rate
        feature_types
        employee_based_advisors
        wm_cost_of_equity_mid
        li_cost_of_equity_mid
        pc_cost_of_equity_mid
        group_cost_of_equity_mid
        rj_company_specific_risk
        rj_company_specific_risk_secondary
        rj_use_owners_comp_direct_expenses
        rjas_benchmark_expenses
        confidence_revenue_weight
        confidence_expenses_weight
        confidence_normalization_weight
        revenue_line_terminology
        multi_line_expense_categories {
          id
          key
          label
          description
          sortOrder
          groupName
          enabled
        }
      }
    }
  }
`;

const RECALCULATE_MULTI_LINE_VALUATION = gql`
  mutation RecalculateMultiLineValuation($input: MultiLineValuationInput!, $target_user_id: ID) {
    recalculateMultiLineValuation(input: $input, target_user_id: $target_user_id) {
      combined
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      summary_metrics
      weights
      features
      lines {
        line
        methods
        weighted
        adjusted
        factors
      }
    }
  }
`;

const SAVE_MULTI_LINE_VALUATION = gql`
  mutation SaveMultiLineValuation($input: MultiLineValuationInput!, $target_user_id: ID) {
    saveMultiLineValuation(input: $input, target_user_id: $target_user_id) {
      combined
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      summary_metrics
      weights
      features
      vault_document_id
      vault_document_url
      lines {
        line
        methods
        weighted
        adjusted
        factors
      }
    }
  }
`;

const UPDATE_MULTI_LINE_VALUATION = gql`
  mutation UpdateMultiLineValuation($id: ID!, $input: MultiLineValuationInput!, $target_user_id: ID) {
    updateMultiLineValuation(id: $id, input: $input, target_user_id: $target_user_id) {
      combined
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      summary_metrics
      weights
      features
      vault_document_id
      vault_document_url
      lines {
        line
        methods
        weighted
        adjusted
        factors
      }
    }
  }
`;

const defaultLines = [
  { name: 'wealth' },
  { name: 'life' },
  { name: 'pc' },
  { name: 'group' },
];

// Derive user defaults from raw user fields (same logic as MultiLineValuationContainer)
// IMPORTANT: Include operatingExpenses so the backend uses the target user's gross_expenses,
// not the admin's (which would be 0)
const deriveUserDefaults = (rawFields, allowManualGrowth) => {
  if (!rawFields) return { wealth: {}, life: {}, pc: {}, group: {} };
  
  const pct = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return undefined;
    return n > 1 ? n / 100 : n;
  };
  
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  };
  
  const compactAvgGrowth = (arr) => {
    const vals = (arr || []).map((v) => Number(v || 0)).filter((v) => Number.isFinite(v));
    const cleaned = vals.filter((v) => v);
    if (cleaned.length < 2) return undefined;
    const growths = [];
    for (let i = 0; i < cleaned.length - 1; i++) {
      const current = cleaned[i];
      const next = cleaned[i + 1];
      if (current && Number.isFinite(current) && Number.isFinite(next)) {
        growths.push((current - next) / current);
      }
    }
    if (!growths.length) return undefined;
    const avg = growths.reduce((s, v) => s + v, 0) / cleaned.length;
    if (!Number.isFinite(avg)) return 0;
    return avg;
  };

  const wealthHistory = [rawFields.year_1_gross, rawFields.year_2_gross, rawFields.year_3_gross, rawFields.year_4_gross, rawFields.year_5_gross];
  const lifeHistory = [rawFields.life_year_1_gross, rawFields.life_year_2_gross, rawFields.life_year_3_gross, rawFields.life_year_4_gross, rawFields.life_year_5_gross];
  const pcHistory = [rawFields.pc_year_1_commission, rawFields.pc_year_2_commission, rawFields.pc_year_3_commission, rawFields.pc_year_4_commission, rawFields.pc_year_5_commission];
  const groupHistory = [rawFields.group_year_1_gross, rawFields.group_year_2_gross, rawFields.group_year_3_gross, rawFields.group_year_4_gross, rawFields.group_year_5_gross];
  
  const autoGrowth = (historyArr) => (allowManualGrowth ? undefined : compactAvgGrowth(historyArr));
  
  // Get gross_expenses from raw fields - this is critical for admin view to match user view
  const grossExpenses = num(rawFields.gross_expenses);
  const expenseGrowthRate = pct(rawFields.expense_growth_rate);

  return {
    wealth: {
      feeBasedRevenue: num(rawFields.fee_based_revenue),
      trailCommissions: num(rawFields.trailer_revenue),
      transactionalCommissions: num(rawFields.transactional_revenue),
      otherDirectRevenue: num(rawFields.direct_revenue),
      faRetentionPercent: pct(rawFields.branch_net_payout),
      // Include operatingExpenses so the backend doesn't fall back to admin's gross_expenses
      operatingExpenses: grossExpenses,
      expenseGrowthRate: expenseGrowthRate,
      history: { y1: num(wealthHistory[0]), y2: num(wealthHistory[1]), y3: num(wealthHistory[2]), y4: num(wealthHistory[3]), y5: num(wealthHistory[4]) },
      growth: autoGrowth(wealthHistory),
    },
    life: {
      firstYearCommissions: num(rawFields.li_first_year_commissions),
      renewalCommissions: num(rawFields.li_renewal_commissions),
      overrideCommissions: num(rawFields.li_override_commissions),
      otherRevenue: num(rawFields.li_other_revenues),
      policyCount: num(rawFields.li_policy_count),
      // Include operatingExpenses for all lines so backend uses target user's expenses
      operatingExpenses: grossExpenses,
      history: { y1: num(lifeHistory[0]), y2: num(lifeHistory[1]), y3: num(lifeHistory[2]), y4: num(lifeHistory[3]), y5: num(lifeHistory[4]) },
      growth: autoGrowth(lifeHistory),
    },
    pc: {
      personalPremium: num(rawFields.pc_personal_lines_premium),
      personalCommRate: pct(rawFields.pc_personal_lines_commission_rate),
      commercialPremium: num(rawFields.pc_commercial_lines_premium),
      commercialCommRate: pct(rawFields.pc_commercial_lines_commission_rate),
      retentionPersonal: pct(rawFields.pc_personal_retention_rate),
      retentionCommercial: pct(rawFields.pc_commercial_retention_rate),
      // Include operatingExpenses for all lines so backend uses target user's expenses
      operatingExpenses: grossExpenses,
      history: { y1: num(pcHistory[0]), y2: num(pcHistory[1]), y3: num(pcHistory[2]), y4: num(pcHistory[3]), y5: num(pcHistory[4]) },
      growth: autoGrowth(pcHistory),
    },
    group: {
      firstYearCommissions: num(rawFields.group_first_year_commissions),
      renewalCommissions: num(rawFields.group_renewal_commissions),
      overrideCommissions: num(rawFields.group_override_commissions),
      otherRevenue: num(rawFields.group_other_revenues),
      numberOfCases: num(rawFields.group_number_of_cases),
      avgCaseSize: num(rawFields.group_average_case_size),
      retentionRate: pct(rawFields.group_case_retention_rate),
      // Include operatingExpenses for all lines so backend uses target user's expenses
      operatingExpenses: grossExpenses,
      history: { y1: num(groupHistory[0]), y2: num(groupHistory[1]), y3: num(groupHistory[2]), y4: num(groupHistory[3]), y5: num(groupHistory[4]) },
      growth: autoGrowth(groupHistory),
    },
  };
};

// Inner component that uses hooks
const AdminUserValuationInner = ({ userId, userName, dataSource, inventoryUserId }) => {
  const apolloClient = useApolloClient();
  const { data: valuationData, loading, error, refetch } = useQuery(
    ADMIN_USER_VALUATION_QUERY,
    {
      variables: { 
        user_id: userId,
        data_source: dataSource,
        inventory_user_id: inventoryUserId,
      },
      skip: !userId,
      fetchPolicy: 'network-only',
    }
  );

  const [saveMutation, { loading: saving }] = useMutation(SAVE_MULTI_LINE_VALUATION);
  const [updateMutation, { loading: updating }] = useMutation(UPDATE_MULTI_LINE_VALUATION);

  const adminUserValuation = valuationData?.admin_user_valuation;
  const lineInputs = adminUserValuation?.line_inputs || {};
  const valuationResult = adminUserValuation?.valuation_result;
  const rawUserFields = adminUserValuation?.raw_user_fields;
  const company = adminUserValuation?.company;
  const allowManualGrowth = company?.allow_user_to_specify_revenue_growth_rate;

  // State for displayed result and loaded scenario
  const [displayedResult, setDisplayedResult] = React.useState(null);
  const [loadedScenarioId, setLoadedScenarioId] = React.useState(null);
  const [loadedScenarioName, setLoadedScenarioName] = React.useState(null);
  const [activeInputs, setActiveInputs] = React.useState(null);
  const [activeExpenses, setActiveExpenses] = React.useState(null);
  const didInitialRecalcRef = React.useRef(false);
  const didInitialExpensesRef = React.useRef(false); // Track if we've set initial expenses

  // Determine if benchmark toggle should be enabled by default (same logic as MultiLineValuation)
  const benchmarkToggleAvailable = React.useMemo(() => {
    if (!company) return false;
    const employeeBasedAdvisors = !!company.employee_based_advisors;
    const featureTypes = company.feature_types || [];
    const hasRjasBenchmarkExpenses = Array.isArray(featureTypes) && featureTypes.includes('rjas_benchmark_expenses');
    return !employeeBasedAdvisors || hasRjasBenchmarkExpenses;
  }, [company]);

  // Determine if we should pass initialExpenses to MultiLineValuation
  // If benchmark needs calculation, pass null so MultiLineValuation's auto-benchmark effect handles it
  // This ensures we use the same calculation path as the user view
  const shouldLetAutoBenchmarkRun = React.useMemo(() => {
    // For saved scenarios with useBenchmark=true but no operatingExpenses, let auto-benchmark run
    if (adminUserValuation?.has_saved_valuation && adminUserValuation.ebitda_adjustments) {
      const saved = adminUserValuation.ebitda_adjustments;
      if (saved.useBenchmark && (!saved.operatingExpenses || saved.operatingExpenses === 0)) {
        console.log('[AdminValuation] Saved scenario needs benchmark recalc - letting auto-benchmark handle it');
        return true;
      }
      return false; // Use saved expenses as-is
    }
    // For new valuations with benchmark available, let auto-benchmark run
    if (benchmarkToggleAvailable) {
      console.log('[AdminValuation] New valuation with benchmark - letting auto-benchmark handle it');
      return true;
    }
    return false;
  }, [adminUserValuation, benchmarkToggleAvailable]);

  // Only provide initialExpenses for saved scenarios that have valid expense data
  // For new valuations or scenarios needing benchmark calc, pass null to let auto-benchmark run
  const computedInitialExpenses = React.useMemo(() => {
    if (shouldLetAutoBenchmarkRun) {
      return null; // Let MultiLineValuation's auto-benchmark effect handle it
    }
    
    // Use saved scenario expenses as-is (they have valid operatingExpenses)
    if (adminUserValuation?.has_saved_valuation && adminUserValuation.ebitda_adjustments) {
      return adminUserValuation.ebitda_adjustments;
    }
    
    return null;
  }, [adminUserValuation, shouldLetAutoBenchmarkRun]);

  // Use computed initial expenses if activeExpenses hasn't been set yet
  const effectiveExpenses = activeExpenses !== null ? activeExpenses : computedInitialExpenses;
  
  console.log('[AdminValuation] effectiveExpenses:', effectiveExpenses, 
    'shouldLetAutoBenchmarkRun:', shouldLetAutoBenchmarkRun,
    'benchmarkToggleAvailable:', benchmarkToggleAvailable);

  // Initialize state when data loads
  React.useEffect(() => {
    if (adminUserValuation) {
      if (adminUserValuation.has_saved_valuation) {
        // Use saved scenario data
        setActiveInputs(lineInputs);
        // Only set activeExpenses if we're NOT letting auto-benchmark run
        if (!shouldLetAutoBenchmarkRun && !didInitialExpensesRef.current && computedInitialExpenses) {
          didInitialExpensesRef.current = true;
          setActiveExpenses(computedInitialExpenses);
        }
        setDisplayedResult(valuationResult);
        setLoadedScenarioId(adminUserValuation.scenario_id);
        setLoadedScenarioName(adminUserValuation.scenario_name);
      } else {
        // Derive from raw user fields
        const derivedInputs = deriveUserDefaults(rawUserFields, allowManualGrowth);
        setActiveInputs(derivedInputs);
        // Don't set activeExpenses for new valuations - let auto-benchmark handle it
        setDisplayedResult(null);
        setLoadedScenarioId(null);
        setLoadedScenarioName(null);
      }
    }
  }, [adminUserValuation, lineInputs, valuationResult, rawUserFields, allowManualGrowth, computedInitialExpenses, shouldLetAutoBenchmarkRun]);

  // Trigger initial recalculation when no saved valuation exists
  // NOTE: We just trigger a basic recalculation here - the auto-benchmark effect in
  // MultiLineValuation will handle setting up correct benchmark expenses afterward
  React.useEffect(() => {
    if (didInitialRecalcRef.current) return;
    if (!adminUserValuation) return;
    if (!rawUserFields) return;
    if (!company) return;
    
    // Skip if we have a saved valuation with valid result
    if (adminUserValuation.has_saved_valuation && valuationResult) return;
    
    // Build initial inputs from raw user fields
    const derivedInputs = deriveUserDefaults(rawUserFields, allowManualGrowth);
    
    // Check if we have any meaningful wealth data to calculate
    const hasWealthData = (
      derivedInputs.wealth?.feeBasedRevenue > 0 ||
      derivedInputs.wealth?.trailCommissions > 0 ||
      derivedInputs.wealth?.transactionalCommissions > 0 ||
      derivedInputs.wealth?.otherDirectRevenue > 0
    );
    
    if (!hasWealthData) return;
    
    didInitialRecalcRef.current = true;
    
    // Build simple payload - just lines, no pre-calculated expenses
    // The auto-benchmark effect in MultiLineValuation will handle benchmark calculation
    const lines = ['wealth', 'life', 'pc', 'group'].map((lineId) => {
      const inputs = { ...(derivedInputs[lineId] || {}) };
      return { name: lineId, inputs };
    });
    
    console.log('[AdminValuation] Initial recalc FIRING - simple payload, auto-benchmark will handle expenses');
    
    apolloClient.mutate({
      mutation: RECALCULATE_MULTI_LINE_VALUATION,
      variables: { 
        input: { lines }, 
        target_user_id: userId 
      },
    }).then((res) => {
      if (res?.data?.recalculateMultiLineValuation) {
        setDisplayedResult(res.data.recalculateMultiLineValuation);
      }
    }).catch((err) => {
      console.error('Initial recalculation error:', err);
    });
  }, [adminUserValuation, rawUserFields, allowManualGrowth, apolloClient, userId, company, valuationResult]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4" />
          <p className="text-succession-text-secondary">Loading user valuation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center text-red-600">
          <p>Error loading valuation: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!adminUserValuation) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-succession-text-secondary">User valuation not found or not authorized.</p>
          <a
            href="/admin/inventory_users"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg border border-succession-border hover:bg-succession-bg-secondary transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Admin
          </a>
        </div>
      </div>
    );
  }

  const displayName = adminUserValuation.user_name || userName || 'User';
  const profilePicture = adminUserValuation.profile_picture;
  const currentDataSource = adminUserValuation.data_source || dataSource || 'user';
  const isEnterpriseData = currentDataSource === 'enterprise';

  return (
    <SuccessionThemeProvider company={company}>
      {/* Header with back link and user info */}
      <div className="admin-valuation-header bg-succession-bg-primary border-b border-succession-border px-6 py-2">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <a
                href="/admin/inventory_users"
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm text-succession-text-secondary hover:bg-succession-bg-secondary transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Admin
              </a>
              <div className="h-5 w-px bg-succession-border" />
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm overflow-hidden"
                  style={{ backgroundColor: isEnterpriseData ? '#059669' : '#6366F1' }}
                >
                  {profilePicture ? (
                    <img
                      src={S3Image(profilePicture)}
                      alt={displayName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = '';
                      }}
                    />
                  ) : null}
                  <span style={profilePicture ? { display: 'none' } : {}}>
                    {(displayName || 'U')[0].toUpperCase()}
                  </span>
                </div>
                <div className="leading-tight">
                  <h1 className="text-base font-semibold text-succession-text-primary leading-tight">
                    {displayName}'s Valuation
                    {loadedScenarioName && (
                      <span className="text-sm font-normal text-succession-text-secondary ml-2">
                        ({loadedScenarioName})
                      </span>
                    )}
                  </h1>
                  <p className="text-xs text-succession-text-secondary leading-tight">
                    Admin View • Editing Enabled •{' '}
                    <span className={isEnterpriseData ? 'text-emerald-600 font-medium' : 'text-indigo-600 font-medium'}>
                      {isEnterpriseData ? 'Enterprise Supplied Data' : 'User Supplied Data'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Admin editing badge */}
            <div className="flex items-center gap-2">
              {/* Data source badge */}
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                style={{
                  background: isEnterpriseData 
                    ? 'linear-gradient(to right, #059669, #10B981)' 
                    : 'linear-gradient(to right, #6366F1, #8B5CF6)',
                }}
              >
                {isEnterpriseData ? 'Enterprise Data' : 'User Data'}
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                style={{
                  background: 'linear-gradient(to right, #6366F1, #8B5CF6)',
                }}
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
                Admin Edit Mode
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CSS overrides: reset global h1/p styles bleeding into header, and remove top padding from content */}
      <style>{`
        .admin-valuation-header h1 {
          margin: 0 !important;
          padding: 0 !important;
          border-bottom: none !important;
        }
        .admin-valuation-header p {
          margin: 0 !important;
          padding: 0 !important;
        }
        .admin-valuation-content .py-4 {
          padding-top: 0 !important;
        }
        .admin-valuation-content .space-y-6 > *:first-child,
        .admin-valuation-content .space-y-8 > *:first-child {
          margin-top: 0 !important;
        }
      `}</style>
      
      <div className="admin-valuation-content">
        <MultiLineValuation
          loading={false}
          error={null}
          result={displayedResult}
          scenarios={[]}
          initialInputs={activeInputs || deriveUserDefaults(rawUserFields, allowManualGrowth)}
          initialExpenses={effectiveExpenses}
          allowManualGrowth={allowManualGrowth}
          benchmarkToggleAvailable={benchmarkToggleAvailable}
          currentUser={rawUserFields || {}} // Pass target user's fields for benchmark calculation
          // Without this the drawer's input labels fall back to
          // intl ids ('succession.terminology.*') that exist in no locale file, so
          // react-intl renders the raw id. Same source the user-facing route uses.
          terminology={company?.revenue_line_terminology}
          viewerIsPrivileged={true}
          viewerUser={{ name: displayName, company }}
          validationData={null}
          loadedScenarioId={loadedScenarioId}
          loadedScenarioName={loadedScenarioName}
          saving={saving}
          updating={updating}
          readOnly={false} // Enable editing for admin
          showInputDrawer={true}
        onRecalculate={async (lines) => {
          try {
            const input = Array.isArray(lines)
              ? { lines }
              : (lines && typeof lines === 'object' && Array.isArray(lines.lines))
                ? { lines: lines.lines, ...(lines.expenses ? { expenses: lines.expenses } : {}) }
                : { lines: defaultLines };
            
            const res = await apolloClient.mutate({
              mutation: RECALCULATE_MULTI_LINE_VALUATION,
              variables: { input, target_user_id: userId },
            });
            
            if (res?.data?.recalculateMultiLineValuation) {
              setDisplayedResult(res.data.recalculateMultiLineValuation);
            }
          } catch (_e) {
            console.error('Recalculate error:', _e);
          }
        }}
        onSave={async (name, payload) => {
          try {
            const inputPayload = payload.expenses 
              ? { lines: payload.lines, name, expenses: payload.expenses }
              : { lines: payload, name };
            const result = await saveMutation({ 
              variables: { 
                input: inputPayload,
                target_user_id: userId // Admin saves on behalf of the user
              } 
            });
            
            // Refetch to get the new scenario
            await refetch();
            
            // Update state with new scenario
            const savedResult = result?.data?.saveMultiLineValuation;
            if (savedResult) {
              setDisplayedResult(savedResult);
            }
            
            const vaultSaved = savedResult?.vault_document_id;
            return { success: true, vaultSaved: !!vaultSaved, name };
          } catch (error) {
            console.error('Save error:', error);
            return { success: false, error: error.message };
          }
        }}
        onUpdate={async (payload) => {
          if (!loadedScenarioId) return { success: false, error: 'No scenario loaded' };
          try {
            const inputPayload = payload.expenses 
              ? { lines: payload.lines, expenses: payload.expenses }
              : { lines: payload.lines || payload };
            const result = await updateMutation({ 
              variables: { 
                id: loadedScenarioId, 
                input: inputPayload,
                target_user_id: userId // Admin updates on behalf of the user
              } 
            });
            
            if (result?.data?.updateMultiLineValuation) {
              setDisplayedResult(result.data.updateMultiLineValuation);
            }
            
            const vaultSaved = result?.data?.updateMultiLineValuation?.vault_document_id;
            return { success: true, vaultSaved: !!vaultSaved, name: loadedScenarioName };
          } catch (error) {
            console.error('Update error:', error);
            return { success: false, error: error.message };
          }
        }}
        onLoadScenario={() => {}} // Admin view doesn't need scenario switching
        onClearScenario={() => {
          setDisplayedResult(null);
          setActiveInputs(deriveUserDefaults(rawUserFields, allowManualGrowth));
          setActiveExpenses(null);
          setLoadedScenarioId(null);
          setLoadedScenarioName(null);
        }}
        />
      </div>
    </SuccessionThemeProvider>
  );
};

// Main container component
// Note: ApolloProvider is provided by the startup wrapper (AdminUserValuationPage.jsx)
const AdminUserValuationContainer = (props) => {
  const { 
    user_id: userId, 
    user_name: userName,
    data_source: dataSource,
    inventory_user_id: inventoryUserId,
  } = props;

  return (
    <JssProvider jss={jss} generateClassName={generateClassName}>
      <MuiThemeProvider theme={createMuiTheme(themes.findbobTheme)}>
        <Provider store={store}>
          <SnackbarProvider maxSnack={3}>
            <CssBaseline />
            <AdminUserValuationInner 
              userId={userId} 
              userName={userName} 
              dataSource={dataSource}
              inventoryUserId={inventoryUserId}
            />
          </SnackbarProvider>
        </Provider>
      </MuiThemeProvider>
    </JssProvider>
  );
};

export default AdminUserValuationContainer;
